import { useState } from 'react';
import { Loader2, TriangleAlert } from 'lucide-react';
import type { WorkflowDetail, WorkflowStep } from '@vagusx/clients';
import { HttpError } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { usePublishWorkflow } from '@/hooks/use-workflows';

/**
 * Publishing: what the next call will run.
 *
 * Refused while anything blocks — the same check the API holds a publish to,
 * so this list and the server's answer agree. Warnings (a missing Spanish
 * line) are listed and do not block: a Spanish caller hears the English line.
 */
export function PublishDialog({
  detail,
  steps,
  open,
  onOpenChange,
}: {
  detail: WorkflowDetail;
  steps: ReadonlyMap<string, WorkflowStep>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const publish = usePublishWorkflow(detail.id);
  const [label, setLabel] = useState('');
  const errors = detail.problemList.filter((p) => p.severity !== 'warning');
  const warnings = detail.problemList.filter((p) => p.severity === 'warning');
  const nodeName = (nodeId: string | undefined): string => {
    if (!nodeId) return 'Workflow';
    const node = detail.draft.nodes.find((n) => n.id === nodeId);
    return (node && steps.get(node.stepId)?.name) ?? nodeId;
  };
  const serverError =
    publish.error instanceof HttpError
      ? publish.error.fieldErrors.length
        ? publish.error.fieldErrors.map((e) => e.message)
        : [publish.error.message]
      : publish.error
        ? [publish.error.message]
        : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Publish {detail.name}</DialogTitle>
          <DialogDescription>
            The next call runs this version. Calls already under way finish on the version they
            started with.
          </DialogDescription>
        </DialogHeader>

        {errors.length > 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-destructive">
              Fix {errors.length} problem{errors.length === 1 ? '' : 's'} first:
            </p>
            <ul className="max-h-60 space-y-1 overflow-y-auto text-sm">
              {errors.map((p, i) => (
                <li key={i} className="flex gap-2">
                  <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                  <span>
                    <span className="font-medium">{nodeName(p.nodeId)}:</span> {p.message}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1">
              <label htmlFor="publish-label" className="text-sm font-medium">
                What changed
              </label>
              <Input
                id="publish-label"
                value={label}
                placeholder="Shorter greeting"
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            {warnings.length > 0 && (
              <div className="space-y-1 text-xs text-muted-foreground">
                <p>Publishing anyway is fine, but:</p>
                <ul className="max-h-40 space-y-0.5 overflow-y-auto">
                  {warnings.map((p, i) => (
                    <li key={i}>
                      {nodeName(p.nodeId)}: {p.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {serverError.map((m, i) => (
              <p key={i} className="text-sm text-destructive">
                {m}
              </p>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={errors.length > 0 || !label.trim() || publish.isPending}
            onClick={() =>
              publish.mutate(
                { expectedRevision: detail.revision, label: label.trim() },
                {
                  onSuccess: () => {
                    setLabel('');
                    onOpenChange(false);
                  },
                }
              )
            }
          >
            {publish.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
